import { useCallback } from 'react'
import { giveGenericHandleChangedData } from 'src/consumer/helpers/utilFunctions'

export default function useHeaderContentHandlers(setPrescriptionTemplate) {
  // Handle column-level changes (type, imageId, imagePadding, imagePaddingTop)
  const handleHeaderColumnChange = useCallback(
    (e, columnIdx) => {
      setPrescriptionTemplate((prev) => {
        const headerColumns = prev?.header?.headerColumns || []
        const finalArray = Array.from(
          { length: Math.max(headerColumns.length, columnIdx + 1) },
          (_, index) => headerColumns[index] || { type: 'text', text: [] },
        )

        return {
          ...prev,
          header: {
            ...(prev?.header || {}),
            headerColumns: finalArray.map((column, index) =>
              index === columnIdx ? giveGenericHandleChangedData(e, column) : column,
            ),
          },
        }
      })
    },
    [setPrescriptionTemplate],
  )

  // Handle text array items within a column
  const handleTextItemChange = useCallback(
    (eventOrUpdater, columnIdx, textIdx) => {
      setPrescriptionTemplate((prev) => {
        const headerColumns = prev?.header?.headerColumns || []
        const column = headerColumns[columnIdx] || { type: 'text', text: [] }
        const textArray = column.text || []

        const finalTextArray = Array.from(
          { length: Math.max(textArray.length, textIdx + 1) },
          (_, index) => textArray[index] || {},
        )

        const updatedColumn = {
          ...column,
          text: finalTextArray.map((textItem, index) =>
            index === textIdx ? giveGenericHandleChangedData(eventOrUpdater, textItem) : textItem,
          ),
        }

        return {
          ...prev,
          header: {
            ...(prev?.header || {}),
            headerColumns: headerColumns.map((col, idx) => (idx === columnIdx ? updatedColumn : col)),
          },
        }
      })
    },
    [setPrescriptionTemplate],
  )

  // Add new text item to column
  const handleAddTextItem = useCallback(
    (columnIdx) => {
      setPrescriptionTemplate((prev) => {
        const headerColumns = prev?.header?.headerColumns || []

        return {
          ...prev,
          header: {
            ...(prev?.header || {}),
            headerColumns: headerColumns.map((col, idx) =>
              idx === columnIdx ? { ...col, text: [...(col.text || []), {}] } : col,
            ),
          },
        }
      })
    },
    [setPrescriptionTemplate],
  )

  // Delete text item from column
  const handleDeleteTextItem = useCallback(
    (columnIdx, textIdx) => {
      setPrescriptionTemplate((prev) => {
        const headerColumns = prev?.header?.headerColumns || []

        return {
          ...prev,
          header: {
            ...(prev?.header || {}),
            headerColumns: headerColumns.map((col, idx) =>
              idx === columnIdx ? { ...col, text: col.text.filter((_, i) => i !== textIdx) } : col,
            ),
          },
        }
      })
    },
    [setPrescriptionTemplate],
  )

  // Add new column
  const handleAddColumn = useCallback(() => {
    setPrescriptionTemplate((prev) => ({
      ...prev,
      header: {
        ...(prev?.header || {}),
        headerColumns: [...(prev?.header?.headerColumns || []), { type: 'text', text: [{}] }],
      },
    }))
  }, [setPrescriptionTemplate])

  // Delete column
  const handleDeleteColumn = useCallback(
    (columnIdx) => {
      setPrescriptionTemplate((prev) => ({
        ...prev,
        header: {
          ...(prev?.header || {}),
          headerColumns: (prev?.header?.headerColumns || []).filter((_, i) => i !== columnIdx),
        },
      }))
    },
    [setPrescriptionTemplate],
  )

  // Factory for text item setter (for TextPropertiesEditor)
  const createTextItemSetter = useCallback(
    (columnIdx, textIdx) => {
      return (eventOrUpdater) => {
        handleTextItemChange(eventOrUpdater, columnIdx, textIdx)
      }
    },
    [handleTextItemChange],
  )

  return {
    handleHeaderColumnChange,
    handleTextItemChange,
    handleAddTextItem,
    handleDeleteTextItem,
    handleAddColumn,
    handleDeleteColumn,
    createTextItemSetter,
  }
}
